// How a position is written down — and, inseparably, what reference system it
// is written down IN.
//
// WHY THIS IS A PREFERENCE AND NOT A HOUSE STYLE. Survey deliverables do not
// agree on how to write a position, and the disagreement is regional rather than
// sloppy. docs/DELIVERABLES.md records three real conventions from real reports
// in this client's own corpus: degrees-and-decimal-minutes with a hemisphere
// prefix for their Ghana work, decimal degrees for the Guyana 2014 work, and
// UTM easting-northing in metres for the later Guyana reporting, whose §1.3
// "Coordinate Reference System" is a numbered top-level section standing before
// any science. A console that prints one spelling forces whoever is transcribing
// into another to do the arithmetic by hand, which is where transcription errors
// come from.
//
// THE DATUM DOES NOT CHANGE, AND THAT IS THE POINT. Every format here describes
// the SAME position on WGS 84. Changing the way a number is spelled is free;
// changing its datum is not, and this module deliberately offers no way to do
// it. The same corpus notes US Gulf work reported on NAD 27, which sits about
// 100 m from WGS 84 in that region — printing a WGS 84 number under a NAD 27
// heading would be a position error large enough to matter, produced by a
// display setting. A datum change needs a real transformation and its own
// evidence; until there is one, we state the datum we have.
//
// WHAT THE PRECISION MEANS. These formats resolve the POSITION to a few metres.
// They say nothing about the forecast at it, which comes from a 0.25° cell about
// 28 km across. The position is exact because it is where somebody clicked or
// typed; the weather there is a cell average, and every panel that prints one
// says so separately.

import { getPref, setPref, subscribePref } from "./prefs.js";

export type PositionFormat = "dd" | "ddm" | "dms" | "utm";

const PREF_KEY = "position";

/** The console's spelling. Unrecognised values fall back to decimal degrees —
 *  what the console printed before this preference existed. */
export function getPositionFormat(): PositionFormat {
  const v = getPref(PREF_KEY);
  return v === "ddm" || v === "dms" || v === "utm" ? v : "dd";
}

export function setPositionFormat(f: PositionFormat): void {
  setPref(PREF_KEY, f);
}

/** Subscribe to format changes. Returns the unsubscribe. */
export function subscribePosition(fn: () => void): () => void {
  return subscribePref(PREF_KEY, () => fn());
}

// --- WGS 84 ------------------------------------------------------------------

const A = 6378137.0; // semi-major axis, metres
const F = 1 / 298.257223563; // flattening
const K0 = 0.9996; // UTM scale factor on the central meridian

/**
 * UTM zone number for a position, INCLUDING the two irregular regions.
 *
 * The exceptions are not trivia: zone 32 is widened over southern Norway, and
 * the Svalbard zones are re-cut, both by international convention. A point at
 * 60 N 5 E is in zone 32, not the 31 that arithmetic alone gives — get this
 * wrong and the easting is out by hundreds of kilometres while looking entirely
 * plausible.
 */
export function utmZone(lat: number, lon: number): number {
  const l = ((((lon + 180) % 360) + 360) % 360) - 180; // normalise to [-180, 180)
  let zone = Math.floor((l + 180) / 6) + 1;
  if (lat >= 56 && lat < 64 && l >= 3 && l < 12) zone = 32; // southern Norway
  if (lat >= 72 && lat < 84) {
    // Svalbard
    if (l >= 0 && l < 9) zone = 31;
    else if (l >= 9 && l < 21) zone = 33;
    else if (l >= 21 && l < 33) zone = 35;
    else if (l >= 33 && l < 42) zone = 37;
  }
  return zone;
}

export interface UtmPoint {
  zone: number;
  /** True in the northern hemisphere — decides the false northing and the EPSG. */
  north: boolean;
  easting: number;
  northing: number;
}

/**
 * WGS 84 geographic → UTM, by the Krüger series.
 *
 * Series to fourth order, which holds sub-millimetre inside a zone — far past
 * anything a forecast console can justify, but the arithmetic is cheap and a
 * coordinate that a survey company will paste into a deliverable is the wrong
 * place to be approximate. Pinned by test against pyproj/PROJ in
 * test/position.test.ts, including the Norway exception.
 */
export function toUtm(lat: number, lon: number): UtmPoint {
  const zone = utmZone(lat, lon);
  const lon0 = ((zone - 1) * 6 - 180 + 3) * (Math.PI / 180); // zone's central meridian
  const phi = lat * (Math.PI / 180);
  const lam = lon * (Math.PI / 180);

  const n = F / (2 - F);
  const n2 = n * n;
  const n3 = n2 * n;
  const n4 = n3 * n;
  const AA = (A / (1 + n)) * (1 + n2 / 4 + n4 / 64);

  const a1 = n / 2 - (2 / 3) * n2 + (5 / 16) * n3;
  const a2 = (13 / 48) * n2 - (3 / 5) * n3;
  const a3 = (61 / 240) * n3;
  const a4 = (49561 / 161280) * n4;

  const dLam = lam - lon0;
  const sinPhi = Math.sin(phi);
  const twoSqrtN = (2 * Math.sqrt(n)) / (1 + n);
  const t = Math.sinh(Math.atanh(sinPhi) - twoSqrtN * Math.atanh(twoSqrtN * sinPhi));
  const xiP = Math.atan2(t, Math.cos(dLam));
  const etaP = Math.atanh(Math.sin(dLam) / Math.sqrt(1 + t * t));

  const alphas = [a1, a2, a3, a4];
  let xi = xiP;
  let eta = etaP;
  for (let j = 1; j <= 4; j++) {
    const aj = alphas[j - 1] as number;
    xi += aj * Math.sin(2 * j * xiP) * Math.cosh(2 * j * etaP);
    eta += aj * Math.cos(2 * j * xiP) * Math.sinh(2 * j * etaP);
  }

  const north = lat >= 0;
  return {
    zone,
    north,
    easting: 500000 + K0 * AA * eta,
    northing: K0 * AA * xi + (north ? 0 : 10000000),
  };
}

/** The EPSG code for a point's UTM zone: 326xx north, 327xx south. */
export function utmEpsg(p: UtmPoint): number {
  return (p.north ? 32600 : 32700) + p.zone;
}

// --- spellings ---------------------------------------------------------------

function ddOne(v: number, pos: string, neg: string): string {
  return `${Math.abs(v).toFixed(3)}°${v >= 0 ? pos : neg}`;
}

function ddmOne(v: number, pos: string, neg: string): string {
  const abs = Math.abs(v);
  let deg = Math.floor(abs);
  let min = (abs - deg) * 60;
  // 59.9996′ must not print as "60.000′" — carry it into the degree.
  if (min >= 59.9995) {
    deg += 1;
    min = 0;
  }
  return `${deg}°${min.toFixed(3)}′${v >= 0 ? pos : neg}`;
}

function dmsOne(v: number, pos: string, neg: string): string {
  const abs = Math.abs(v);
  let deg = Math.floor(abs);
  let min = Math.floor((abs - deg) * 60);
  let sec = (abs - deg - min / 60) * 3600;
  if (sec >= 59.95) {
    sec = 0;
    min += 1;
  }
  if (min >= 60) {
    min = 0;
    deg += 1;
  }
  return `${deg}°${String(min).padStart(2, "0")}′${sec.toFixed(1).padStart(4, "0")}″${v >= 0 ? pos : neg}`;
}

/** Write a position in a NAMED format, whatever the console's preference is —
 *  the printable report needs this so a document can be produced in the client's
 *  convention without flipping the console into it. */
export function fmtPositionIn(lat: number, lon: number, f: PositionFormat): string {
  switch (f) {
    case "ddm":
      return `${ddmOne(lat, "N", "S")} ${ddmOne(lon, "E", "W")}`;
    case "dms":
      return `${dmsOne(lat, "N", "S")} ${dmsOne(lon, "E", "W")}`;
    case "utm": {
      const p = toUtm(lat, lon);
      // Metres, whole, with the axis letter on each: the spelling their Guyana
      // reporting uses. Never abbreviated to a bare pair — an easting and a
      // northing are indistinguishable by magnitude in the tropics.
      return `${p.zone}${p.north ? "N" : "S"} ${Math.round(p.easting)}E ${Math.round(p.northing)}N`;
    }
    default:
      return `${ddOne(lat, "N", "S")} ${ddOne(lon, "E", "W")}`;
  }
}

/** Write a position in the console's chosen format. */
export function fmtPosition(lat: number, lon: number): string {
  return fmtPositionIn(lat, lon, getPositionFormat());
}

/**
 * The reference system a position is stated in, as it should appear beside one.
 *
 * ALWAYS AVAILABLE, because a position without its datum is not a position — it
 * is a number that looks like one. Their own corpus contains a table of station
 * coordinates with no datum stated, which docs/DELIVERABLES.md flags; the
 * reports that take position seriously give the CRS a numbered section before
 * any science. This is the one-line version of that section.
 */
export function positionCrs(lat: number, lon: number, f: PositionFormat = getPositionFormat()): string {
  if (f !== "utm") return "WGS 84 (EPSG:4326)";
  const p = toUtm(lat, lon);
  return `WGS 84 / UTM zone ${p.zone}${p.north ? "N" : "S"} (EPSG:${utmEpsg(p)})`;
}

/** Menu labels, in the order they are offered. */
export const POSITION_FORMATS: readonly (readonly [PositionFormat, string, string])[] = [
  ["dd", "Decimal", "27.500°N 94.200°W — decimal degrees"],
  ["ddm", "Deg-min", "27°30.000′N 94°12.000′W — degrees and decimal minutes, the surveyor's spelling"],
  ["dms", "DMS", "27°30′00.0″N 94°12′00.0″W — degrees, minutes and seconds"],
  ["utm", "UTM", "15N 381468E 3042390N — zone easting/northing in metres, on WGS 84"],
];
